import { Form as InertiaForm } from '@inertiajs/react'
import { type ChangeEvent, type ComponentProps, useMemo, useState } from 'react'
import { SelectField, TextField } from './form-fields.tsx'

import type {
  AreaOptionType,
  AreasByCommunityType,
  CommunityOptionType,
  TaxPayerType,
  TaxPayerTypeOptionType,
  TaxPayerTypesByCommunityType,
} from './types.ts'

type FormProps = Omit<ComponentProps<typeof InertiaForm>, 'children'> & {
  tax_payer: TaxPayerType
  tax_payer_types?: TaxPayerTypeOptionType[]
  tax_payer_types_by_community?: TaxPayerTypesByCommunityType
  areas?: AreaOptionType[]
  areas_by_community?: AreasByCommunityType
  communities?: CommunityOptionType[]
  submitText: string
}

export default function Form({
  tax_payer,
  tax_payer_types = [],
  tax_payer_types_by_community,
  areas = [],
  areas_by_community,
  communities = [],
  submitText,
  ...formProps
}: FormProps) {
  const [selectedCommunityId, setSelectedCommunityId] = useState(tax_payer.community_id || communities[0]?.id || "")
  const [selectedTaxPayerTypeId, setSelectedTaxPayerTypeId] = useState(tax_payer.tax_payer_type_id || "")
  const [selectedAreaId, setSelectedAreaId] = useState(tax_payer.area_id || "")
  const filteredTaxPayerTypes = useMemo(() => {
    if (!tax_payer_types_by_community || communities.length === 0) {
      return tax_payer_types
    }

    return tax_payer_types_by_community[selectedCommunityId] || []
  }, [communities.length, selectedCommunityId, tax_payer_types, tax_payer_types_by_community])
  const filteredAreas = useMemo(() => {
    if (!areas_by_community || communities.length === 0) {
      return areas
    }

    return areas_by_community[selectedCommunityId] || []
  }, [areas, areas_by_community, communities.length, selectedCommunityId])

  const handleCommunityChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedCommunityId(event.target.value)
    setSelectedTaxPayerTypeId("")
    setSelectedAreaId("")
  }

  return (
    <InertiaForm
      transform={data => ({ tax_payer: data })}
      className="contents"
      {...formProps}
    >
      {({ errors, processing }) => (
        <>
          <TextField
            label="Nom"
            name="name"
            id="name"
            defaultValue={tax_payer.name}
            error={errors.name}
          />

          <TextField
            label="Nom complet"
            name="full_name"
            id="full_name"
            defaultValue={tax_payer.full_name}
            error={errors.full_name}
          />


          {communities.length > 0 && (
            <div className="my-5">
              <label htmlFor="community_id">Commune</label>
              <select
                name="community_id"
                id="community_id"
                value={selectedCommunityId}
                onChange={handleCommunityChange}
                className="block shadow rounded-md border border-gray-400 outline-none px-3 py-2 mt-2 w-full"
                required
              >
                <option value="" disabled>Sélectionner une commune</option>
                {communities.map((community) => (
                  <option key={community.id} value={community.id}>
                    {community.name} ({community.code})
                  </option>
                ))}
              </select>
              {errors.community_id && (
                <div className="text-red-500 px-3 py-2 font-medium">
                  {errors.community_id.join(', ')}
                </div>
              )}
            </div>
          )}


          <SelectField
            label="Type de contribuable"
            name="tax_payer_type_id"
            id="tax_payer_type_id"
            value={selectedTaxPayerTypeId}
            onChange={event => setSelectedTaxPayerTypeId(event.target.value)}
            placeholder={filteredTaxPayerTypes.length > 0 ? "Sélectionner un type de contribuable" : "Aucun type disponible"}
            options={filteredTaxPayerTypes}
            disabled={filteredTaxPayerTypes.length === 0}
            error={errors.tax_payer_type_id}
          />

          <SelectField
            label="Zone"
            name="area_id"
            id="area_id"
            value={selectedAreaId}
            onChange={event => setSelectedAreaId(event.target.value)}
            placeholder={filteredAreas.length > 0 ? "Sélectionner une zone" : "Aucune zone disponible"}
            options={filteredAreas}
            disabled={filteredAreas.length === 0}
            error={errors.area_id}
          />

          <TextField
            label="Adresse"
            name="address"
            id="address"
            defaultValue={tax_payer.address}
            error={errors.address}
          />
          <input type="hidden" name="lat" value={tax_payer.lat ?? ""} />
          <input type="hidden" name="lng" value={tax_payer.lng ?? ""} />

          <TextField
            label="RCCM"
            name="rccm"
            id="rccm"
            defaultValue={tax_payer.rccm}
            error={errors.rccm}
          />

          <TextField
            label="NIF"
            name="nif"
            id="nif"
            defaultValue={tax_payer.nif}
            error={errors.nif}
          />

          <TextField
            label="Téléphone"
            name="phone"
            id="phone"
            defaultValue={tax_payer.phone}
            error={errors.phone}
          />

          <TextField
            label="Adresse e-mail"
            name="email"
            id="email"
            defaultValue={tax_payer.email}
            error={errors.email}
          />



          <div className="my-5">
            <label htmlFor="verified">Vérifié</label>
            <input
              type="checkbox"
              name="verified"
              id="verified"
              defaultChecked={tax_payer.verified}
              className="block mt-2 h-5 w-5"
            />
            {errors.verified && (
              <div className="text-red-500 px-3 py-2 font-medium">
                {errors.verified.join(', ')}
              </div>
            )}
          </div>


          <div className="inline">
            <button
              type="submit"
              disabled={processing}
              className="rounded-lg py-3 px-5 bg-blue-600 text-white inline-block font-medium cursor-pointer"
            >
              {submitText}
            </button>
          </div>
        </>
      )}
    </InertiaForm>
  )
}
